import { TGovbrValidationOutcome } from "../../../_services/interface/credential.interface";

/**
 * GEN-3107 fase 2 — o que a tela diz para cada desfecho da validação da conta gov.br.
 *
 * Isolado do componente de propósito: **de quem é a culpa** é regra de produto, não detalhe de
 * render, e errar isso é o defeito mais caro deste fluxo. Duas trocas possíveis, as duas ruins:
 *
 * - falha NOSSA apresentada como erro de dados → o cliente passa a duvidar de uma senha que
 *   está correta, troca a senha no gov.br, e o problema (nosso) continua;
 * - erro do cliente apresentado como "instabilidade" → ele tenta de novo indefinidamente, e
 *   cada tentativa é um login real na conta dele e um crédito de captcha nosso.
 *
 * ⚠️ `duplo_fator` não é nenhum dos dois. A senha está certa e não há defeito: a conta tem
 * segundo fator. O texto orienta e aponta a saída, sem acusar ninguém.
 *
 * ⚠️ GEN-3746 — a saída MUDOU. Antes era o navegador transmitido; agora é desligar o segundo
 * fator na conta gov.br, e vale para os DOIS caminhos (senha e certificado). Decisão do Gustav
 * em 24/09/2026: a automação passa a exigir 2FA desligado, ponto.
 *
 * Consequência registrada: quem conectava por tela transmitida com 2FA ligado deixa de ter
 * essa saída oferecida aqui. A tela transmitida continua existindo no produto — o que sai é a
 * RECOMENDAÇÃO dela neste desfecho.
 */

/**
 * `status` aceito pelo `CustomAlert` neste fluxo.
 *
 * ⚠️ `"info"` FICOU DE FORA de propósito. No `type="three"` — o padrão da casa, que este fluxo
 * usa — `status="info"` renderiza um SPINNER GIRANDO em vez de um ícone de informação: o aviso
 * de "não conseguimos validar agora" ficaria com cara de "ainda estamos tentando", que é o
 * oposto do que ele diz. Aviso neutro é `"warning"`. Mesma armadilha registrada em
 * `ConfigPanel/content/Queries/Queries.tsx`.
 */
export type TGovbrValidationTone = "success" | "error" | "warning";

export interface IGovbrValidationMessage {
  /** `status` do `CustomAlert`. */
  tone: TGovbrValidationTone;
  titulo: string;
  /** Texto do corpo. Sem HTML — quem renderiza decide a marcação. */
  detalhe: string;
  /** Oferecer o botão de tentar de novo? Só quando repetir pode mudar o resultado. */
  permiteNovaTentativa: boolean;
}

const MENSAGENS: Record<TGovbrValidationOutcome, IGovbrValidationMessage> = {
  reconectada: {
    tone: "success",
    titulo: "Conta gov.br conectada",
    detalhe: "Validamos o acesso e as consultas desta credencial já rodam automaticamente.",
    permiteNovaTentativa: false,
  },

  credencial_recusada: {
    tone: "error",
    titulo: "O gov.br não aceitou esses dados",
    // Diz o que fazer, e não só o que falhou. E não afirma qual dos dois está errado: o
    // portal não conta, e chutar "senha incorreta" manda o cliente trocar a senha à toa.
    detalhe:
      "Confira o CPF e a senha da conta gov.br e salve novamente. " +
      "Se você mudou a senha recentemente, é ela que precisa ser atualizada aqui.",
    permiteNovaTentativa: false,
  },

  duplo_fator: {
    tone: "warning",
    titulo: "Desative a verificação em duas etapas",
    // Não acusa: o acesso está correto. Diz o que precisa mudar, ONDE muda (é na conta do
    // cliente, não aqui) e que depois é só tentar de novo. Vale igual para senha e para
    // certificado — o segundo fator é da conta gov.br, não do meio de acesso.
    detalhe:
      "Seu acesso está correto. Para as consultas rodarem sozinhas, a conta gov.br do titular " +
      "precisa estar sem verificação em duas etapas. Desative no gov.br e salve novamente.",
    // Retentar sem desligar o 2FA repete o mesmo desfecho e gasta um login real na conta do
    // titular — a ação que resolve é do cliente, fora do nosso fluxo.
    permiteNovaTentativa: false,
  },

  falha_nossa: {
    tone: "warning",
    titulo: "Não conseguimos validar agora",
    // ⚠️ Não é erro do cliente e o texto não pode sugerir que seja. Nada de "confira seus
    // dados": os dados podem estar perfeitos e o problema é do nosso lado.
    detalhe: "O problema é do nosso lado, não dos seus dados. Tente novamente em alguns instantes.",
    permiteNovaTentativa: true,
  },

  desligado: {
    tone: "warning",
    titulo: "Conexão indisponível no momento",
    // ⚠️ GEN-3754 — a tela transmitida saiu do fluxo (decisão de 13/10/2026), então este desfecho
    // não tem mais saída alternativa: é falha NOSSA de configuração, e o texto diz isso.
    detalhe: "A conexão automática está fora do ar. O problema é do nosso lado — tente novamente mais tarde.",
    // Retentar agora bate no MESMO solver que está fora.
    permiteNovaTentativa: false,
  },
};

/**
 * Mensagem de um desfecho. Desconhecido cai em `falha_nossa`.
 *
 * A queda é deliberada e assimétrica: entre acusar o cliente e assumir a culpa por um desfecho
 * que a tela ainda não sabe ler, assumir é o erro mais barato — e o único que não faz uma
 * pessoa mexer numa credencial que está correta.
 */
export const mensagemDaValidacaoGovbr = (outcome: string | undefined | null): IGovbrValidationMessage =>
  MENSAGENS[outcome as TGovbrValidationOutcome] ?? MENSAGENS.falha_nossa;

/**
 * GEN-3754 — confirmação de que a conexão começou. O ponto é LIBERAR a pessoa: ela não precisa
 * esperar aqui, e precisa saber onde acompanhar.
 */
export const TEXTO_CONEXAO_INICIADA =
  "Estamos entrando na sua conta gov.br em segundo plano — costuma levar um ou dois minutos, e pode levar alguns a mais se o gov.br pedir uma nova tentativa. Você pode fechar " +
  "esta janela e continuar usando o sistema; acompanhe pelo ícone de conexão na barra superior.";
